import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from './button';

import { ErrorBoundaryIllustration } from '@/shared/components/illustrations/error-boundary-illustration';
import { logger } from '@/shared/lib/logger';
import { captureError, showReportDialog } from '@/shared/observability/sentry-setup';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

/**
 * Error boundary global. Captura erros React que escapam de componentes
 * filhos, registra no logger (com PII redaction) e envia para o Sentry
 * (no-op se DSN ausente).
 *
 * Phase 15 — fallback polido:
 *  - Ilustracao "Glass Shard com fagulhas" (Obsidian Pulse).
 *  - Titulo "Algo deu muito errado" (escala display-lg).
 *  - Mensagem do erro em texto secundario.
 *  - CTA primario "Recarregar pagina" (window.location.reload).
 *  - CTA secundario "Reportar problema" (Sentry showReportDialog — no-op
 *    silencioso se DSN ausente).
 *  - Em DEV, mostra stack trace expandivel via <details/> — auxilia debug
 *    sem poluir prod (em prod nem o componentStack chega na tela).
 *
 * Posicionamento: montado em `_layout.tsx` (root) — captura tudo abaixo.
 *
 * Boundaries adicionais por feature podem ser adicionados se uma sub-arvore
 * precisar de fallback proprio (ex: charts da Phase 10 se quebrarem).
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, componentStack: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    logger.app.error('ErrorBoundary capturou:', error, { componentStack: info.componentStack });
    captureError(error, { componentStack: info.componentStack });
    this.props.onError?.(error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, componentStack: null });
    if (typeof window !== 'undefined') window.location.reload();
  };

  handleReport = () => {
    showReportDialog();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const message = this.state.error?.message ?? 'Algo inesperado aconteceu.';
    const stack = this.state.error?.stack ?? null;
    const componentStack = this.state.componentStack;
    const isDev = import.meta.env.DEV;

    return (
      <main
        role="alert"
        data-testid="error-boundary"
        className="flex min-h-screen flex-col items-center justify-center gap-5 bg-canvas px-6 py-12 text-center"
      >
        <ErrorBoundaryIllustration />
        <p className="text-label-md font-bold uppercase tracking-[0.18em] text-danger">
          Ocorreu um erro
        </p>
        <h1 className="text-display-lg text-on-surface">Algo deu muito errado</h1>
        <p
          className="max-w-md text-body-md text-secondary-text"
          data-testid="error-boundary-message"
        >
          {message}
        </p>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          <Button
            variant="primary"
            size="md"
            onClick={this.handleReload}
            testID="error-boundary-reload"
          >
            Recarregar página
          </Button>
          <Button
            variant="ghost"
            size="md"
            onClick={this.handleReport}
            testID="error-boundary-report"
          >
            Reportar problema
          </Button>
        </div>

        {isDev && (stack || componentStack) ? (
          <details
            className="mt-6 w-full max-w-3xl rounded-card bg-surface-high p-4 text-left"
            data-testid="error-boundary-debug"
          >
            <summary className="cursor-pointer text-label-md font-semibold uppercase tracking-wider text-secondary-text">
              Stack trace (dev only)
            </summary>
            {stack ? (
              <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-all text-label-md text-secondary-text">
                {stack}
              </pre>
            ) : null}
            {componentStack ? (
              <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-all text-label-md text-secondary-text">
                {componentStack}
              </pre>
            ) : null}
          </details>
        ) : null}
      </main>
    );
  }
}
